import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  makeStyles,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { ArrowUpload24Regular, Info16Regular } from '@fluentui/react-icons';
import React, { useRef, useState } from 'react';
import { useUploadAudioMutation } from '../../queries/recording';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  infoIcon: {
    color: tokens.colorBrandForeground1,
    marginTop: '2px',
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
  },
  dropZoneActive: {
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  dropZoneIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalS,
  },
  fileName: {
    marginTop: tokens.spacingVerticalS,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  hiddenInput: {
    display: 'none',
  },
});

interface UploadAudioDialogProps {
  open: boolean;
  onClose: () => void;
}

const ACCEPTED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'video/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/ogg',
  'audio/wav',
  'audio/x-wav',
  'audio/flac',
  'audio/x-flac',
  'audio/webm',
  'video/webm',
  'audio/aac',
  'audio/x-aac',
  'video/quicktime',
];

const ACCEPTED_EXTENSIONS = '.mp3,.mp4,.m4a,.wav,.webm,.ogg,.flac,.aac';

const ACCEPTED_EXTENSIONS_REGEX = /\.(mp3|mp4|m4a|wav|webm|ogg|flac|aac)$/i;

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB

export const UploadAudioDialog = ({
  open,
  onClose,
}: UploadAudioDialogProps) => {
  const styles = useStyles();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync, isPending } = useUploadAudioMutation();
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFileSelect = (selectedFile: File) => {
    setValidationError(null);

    if (selectedFile.size > MAX_FILE_SIZE) {
      setValidationError('Le fichier depasse la limite de 200 Mo.');
      setFile(null);
      return;
    }

    if (
      ACCEPTED_AUDIO_TYPES.includes(selectedFile.type) ||
      selectedFile.name.match(ACCEPTED_EXTENSIONS_REGEX)
    ) {
      setFile(selectedFile);
    } else {
      setValidationError(
        'Format non supporte. Formats acceptes : MP3, MP4, M4A, WAV, WebM, OGG, FLAC, AAC.'
      );
      setFile(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    await mutateAsync({ file });
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setIsDragging(false);
    setValidationError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Importer un enregistrement audio</DialogTitle>
          <DialogContent className={styles.content}>
            <div className={styles.infoBox}>
              <Info16Regular className={styles.infoIcon} />
              <Text size={200}>
                Importez un fichier audio pour le transcrire automatiquement.
                Les intervenants seront identifies automatiquement (Speaker 1,
                Speaker 2, etc.).
              </Text>
            </div>
            <Field label="Fichier audio">
              <div
                className={`${styles.dropZone} ${
                  isDragging ? styles.dropZoneActive : ''
                }`}
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <ArrowUpload24Regular className={styles.dropZoneIcon} />
                <Text>
                  {file
                    ? 'Cliquez pour changer de fichier'
                    : 'Glissez-deposez un fichier ou cliquez pour parcourir'}
                </Text>
                <Text size={200}>
                  Formats acceptes : MP3, MP4, M4A, WAV, WebM, OGG, FLAC, AAC (max 200 Mo)
                </Text>
                {file && <Text className={styles.fileName}>{file.name}</Text>}
              </div>
              {validationError && (
                <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {validationError}
                </Text>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                onChange={handleInputChange}
                className={styles.hiddenInput}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={isPending}
            >
              Annuler
            </Button>
            <Button
              appearance="primary"
              onClick={handleUpload}
              disabled={!file || isPending}
            >
              {isPending ? <Spinner size="tiny" /> : 'Importer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
